import {JobAppointmentSmsSentEventDataApiDto, JobEventApiDto, JobSmsSentEventDataApiDto} from "../../../api/model/rest";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import {option, Option} from "ts-option";
import {default as JobEvent, JobEventTypeEnum, JobSmsEventSubtypeEnum} from "./JobEvent";
import moment = require("moment");
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import {IconColor} from "../../../hiberUiKit/src/molecules/StreamItem/StreamItemIcon";
import {getIndividualContact} from "../../../store/view/contact";
import {store} from "../../../navigation/ReduxProvider";

export default class JobSmsSentEvent extends JobEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {string} jobId job id.
	 * @param {JobEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} entity entity which creates the event.
	 * @param {moment.Moment} creationDate creation date of event.
	 * @param {string} referenceNumber job reference number.
	 * @param data sms send event data
	 */
	constructor(public readonly id: string,
		public readonly jobId: string,
		public readonly type: JobEventTypeEnum,
		public readonly entity: AuthenticatedEntity,
		public readonly creationDate: moment.Moment,
		public readonly referenceNumber: string,
		public readonly data: JobAppointmentSmsSentEventData | JobSmsSentEventData) {
		super(id, jobId, type, entity, creationDate, referenceNumber);
	}

	public static fromJson(json: JobEventApiDto): JobSmsSentEvent {
		let data: JobAppointmentSmsSentEventData | JobSmsSentEventData;
		const jsonData = json.data as JobAppointmentSmsSentEventDataApiDto;
		switch (jsonData.subtype) {
			case JobSmsEventSubtypeEnum.JobAppointmentConfirmationSms:
				data = JobAppointmentSmsSentEventData.fromJson(json.data as JobAppointmentSmsSentEventDataApiDto);
				break;
			case JobSmsEventSubtypeEnum.JobFreeTextContactSms:
				data = JobSmsSentEventData.fromJson(json.data as JobSmsSentEventDataApiDto);
				break;
			case JobSmsEventSubtypeEnum.JobFreeTextCreatorSms:
				data = JobSmsSentEventData.fromJson(json.data as JobSmsSentEventDataApiDto);
				break;
		}
		const entity = AuthenticatedEntity.fromJson(json.entity);
		return new JobSmsSentEvent(json.id,
			json.jobId,
			JobEventTypeEnum.JobSmsSent,
			entity,
			moment(json.createdAt),
			json.referenceNumber,
			data);
	}

	getIcon() {
		return Icon.SMS;
	}

	getIconColor(): IconColor {
		return IconColor.Burgundy;
	}

	getActivityAdditionalMessages(): string[] {
		return [`Message: ${this.data.message}`];
	}

	getActivityType(): string {
		return `SMS`;
	}

	getActivityMessage(): string {
		let eventMessage: string;
		switch (this.data.subtype) {
			case JobSmsEventSubtypeEnum.JobAppointmentConfirmationSms:
				eventMessage = "Appointment confirmation SMS";
				break;
			case JobSmsEventSubtypeEnum.JobFreeTextContactSms:
				eventMessage = "Free text SMS to contact";
				break;
			case JobSmsEventSubtypeEnum.JobFreeTextCreatorSms:
				eventMessage = "Free text SMS to job creator";
				break;
			default:
				eventMessage = "SMS to unknown receiver";
		}

		const receiver = this.data.receiverId.flatMap(r => getIndividualContact(r, store.getState())).map(
			c => ` - ${c.data.displayName()}`).getOrElse("");
		const receiverPhones = this.data.receiverPhones ? ` (${this.data.receiverPhones.join(", ")})` : "";

		return eventMessage + receiver + receiverPhones;
	}
}

export class JobAppointmentSmsSentEventData {
	constructor(public readonly receiverId: Option<string>,
		public readonly receiverPhones: string[],
		public readonly appointmentId: Option<string>,
		public readonly message: string,
		public readonly subtype: string) {}

	public static fromJson(json: JobAppointmentSmsSentEventDataApiDto): JobAppointmentSmsSentEventData {
		return new JobAppointmentSmsSentEventData(option(json.receiverId), json.receiverPhones,
			option(json.appointmentId), json.message, json.subtype);
	}
}

export class JobSmsSentEventData {
	constructor(public readonly receiverId: Option<string>,
		public readonly receiverPhones: string[],
		public readonly message: string,
		public readonly subtype: string) {}

	public static fromJson(json: JobSmsSentEventDataApiDto): JobSmsSentEventData {
		return new JobSmsSentEventData(option(json.receiverId), json.receiverPhones, json.message, json.subtype);
	}
}